import { IQuote, IQuoteLineItem, QuoteUpdateRequest } from "../../shared/types";
import { EditorSection, sectionsChanged, toSectionRequest } from "../../components/QuoteSectionBar/sectionUtils";

export type ProspectDiscountFields = { monthly: string; installment: string; annual: string };

export const hasUnavailablePrice = (items: IQuoteLineItem[]) => items.some(item => item.is_price_unavailable);

// Issue 36: the edited discount fields differ from the quote's saved prospect Discounts.
// Customer quotes have no editable quote-level Discount, so they never report a change here.
export const prospectDiscountsChanged = (quote: IQuote, discounts: ProspectDiscountFields | undefined) =>
  quote.quote_type === "prospect" && discounts != null && (
    Number(discounts.monthly) !== (quote.prospect_monthly_discount ?? 0)
    || Number(discounts.installment) !== (quote.prospect_installment_discount ?? 0)
    || Number(discounts.annual) !== (quote.prospect_annual_discount ?? 0));

const comparableLine = (line: IQuoteLineItem) => ({
  product_id: line.product_id, sku_id: line.sku_id, sku_title: line.sku_title,
  segment: line.segment, term_duration: line.term_duration, quantity: line.quantity,
  note: line.note || null, is_price_manual: line.is_price_manual,
  // Issue 50: compare the raw manual input the user actually edits, not msrp_unit_price/
  // cost_unit_price - those are now always server-computed output, so comparing them would
  // silently stop detecting a real MSRP/Cost edit on a manual line.
  manual_msrp_unit_price: line.is_price_manual ? line.manual_msrp_unit_price : null,
  manual_cost_unit_price: line.is_price_manual ? line.manual_cost_unit_price : null,
  requested_discount_pct: line.is_price_manual ? line.requested_discount_pct ?? null : null,
  // Issue 35: a pass-through change is a real edit. For a partner_center promotion only the
  // source and pass-through are user-controlled (the rest is server-re-resolved); a manual
  // promotion also carries the Microsoft percent, end date and seat band.
  promo_source: line.promo_source ?? null,
  customer_promo_pct: line.promo_source ? line.customer_promo_pct ?? 0 : null,
  microsoft_promo_pct: line.promo_source === "manual" ? line.microsoft_promo_pct ?? null : null,
  promo_end_date: line.promo_source === "manual" ? line.promo_end_date ?? null : null,
  promo_min_quantity: line.promo_source === "manual" ? line.promo_min_quantity ?? null : null,
  promo_max_quantity: line.promo_source === "manual" ? line.promo_max_quantity ?? null : null,
  // Issue 33: a line-level date override is user-controlled; the charge window it produces is not.
  inherits_section_dates: line.inherits_section_dates !== false,
  line_start_date: line.inherits_section_dates === false ? line.start_date ?? null : null,
  line_end_date: line.inherits_section_dates === false ? line.end_date ?? null : null,
  line_end_date_source: line.inherits_section_dates === false ? line.end_date_source ?? null : null,
  line_coterm_target: line.inherits_section_dates === false && line.end_date_source === "coterm"
    ? line.coterm_target_subscription_id ?? null : null,
});

// Issue 31: the affiliate name is stored trimmed + upper-cased; compare on that normalized form
// so re-opening a saved quote never looks "dirty".
export const normalizeAffiliateName = (value: string) => value.trim().toUpperCase() || null;

export const hasUnsavedQuoteChanges = (quote: IQuote, values: {
  name: string; description: string; internalNote: string; sections: EditorSection[]; discounts?: ProspectDiscountFields;
  hasAffiliate?: boolean; affiliateName?: string;
}) => (values.name.trim() || null) !== quote.name
  || (values.description.trim() || null) !== quote.description
  || (values.internalNote.trim() || null) !== quote.internal_note
  || (values.hasAffiliate ?? quote.has_affiliate) !== quote.has_affiliate
  || normalizeAffiliateName(values.affiliateName ?? quote.affiliate_name ?? "") !== (quote.affiliate_name ?? null)
  || prospectDiscountsChanged(quote, values.discounts)
  || sectionsChanged(quote.sections, values.sections, comparableLine);

const prospectDiscount = (quote: IQuote, edited: string | undefined, saved: number | null | undefined) =>
  edited != null && edited !== "" ? Number(edited) : (saved ?? 0);

export const buildUpdateQuoteRequest = (quote: IQuote, values: {
  name: string; description: string; internalNote: string; sections: EditorSection[]; discounts?: ProspectDiscountFields;
  hasAffiliate?: boolean; affiliateName?: string;
}): QuoteUpdateRequest => ({
  quote_type: quote.quote_type,
  customer_id: quote.customer_id,
  has_affiliate: values.hasAffiliate ?? quote.has_affiliate,
  affiliate_name: normalizeAffiliateName(values.affiliateName ?? quote.affiliate_name ?? ""),
  prospect_name: quote.quote_type === "prospect" ? (quote.prospect_name || "") : null,
  prospect_email: quote.quote_type === "prospect" ? (quote.prospect_email || "") : null,
  prospect_monthly_discount: quote.quote_type === "prospect" ? prospectDiscount(quote, values.discounts?.monthly, quote.prospect_monthly_discount) : null,
  prospect_installment_discount: quote.quote_type === "prospect" ? prospectDiscount(quote, values.discounts?.installment, quote.prospect_installment_discount) : null,
  prospect_annual_discount: quote.quote_type === "prospect" ? prospectDiscount(quote, values.discounts?.annual, quote.prospect_annual_discount) : null,
  name: values.name.trim() || null,
  description: values.description.trim() || null,
  internal_note: values.internalNote.trim() || null,
  sections: values.sections.map(toSectionRequest),
});
